/**
 * MSAL account display labels for UI (switcher, headers).
 * Treats placeholder names from IdP (e.g. "unknown") as absent so we prefer email/username.
 */

/**
 * @param {string|null|undefined} email
 * @returns {string}
 */
export function normalizeEmailForMatch(email) {
  if (!email || typeof email !== 'string') return '';
  return email.trim().toLowerCase();
}

/**
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isLikelyUserEmail(value) {
  if (!value || typeof value !== 'string') return false;
  const looksLike = value.includes('@') && value.includes('.') && !/onmicrosoft\.com$/i.test(value);
  const notGuid = !/^[0-9a-fA-F-]{32,}$/.test(value);
  return looksLike && notGuid;
}

/**
 * Login id / UPN shown in account switcher (includes *.onmicrosoft.com; excludes bare GUIDs).
 * MSAL often stores only CIAM/Entra UPN here — {@link getPrimaryReadableEmail} would hide it.
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isEmailLikeForAccountSwitcher(value) {
  if (!value || typeof value !== 'string') return false;
  const t = value.trim();
  if (t.length < 3 || t.length > 360) return false;
  if (!t.includes('@')) return false;
  if (/^[0-9a-fA-F-]{32,}$/.test(t)) return false;
  const at = t.lastIndexOf('@');
  const local = t.slice(0, at);
  const domain = t.slice(at + 1);
  return Boolean(local && domain);
}

/**
 * B2C/CIAM sometimes uses `issuer#email@domain` in a claim; return embedded email when present.
 * @param {string} value
 * @returns {string}
 */
export function extractEmailFromCompoundLoginId(value) {
  if (!value || typeof value !== 'string') return '';
  const t = value.trim();
  if (!t.includes('#') || !t.includes('@')) return '';
  const parts = t.split('#');
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const p = parts[i].trim();
    if (isEmailLikeForAccountSwitcher(p)) return p;
  }
  return '';
}

/** Keys not scanned for embedded emails (JWT metadata, opaque ids, display-only). */
const CLAIM_SCAN_SKIP_KEYS = new Set([
  'sub',
  'nonce',
  'sid',
  'aud',
  'iss',
  'iat',
  'nbf',
  'exp',
  'auth_time',
  'ver',
  'azp',
  'at_hash',
  'c_hash',
  'jti',
  'aio',
  'rh',
  'uti',
  'name',
  'given_name',
  'family_name',
  'nickname',
  'middle_name',
  'picture',
  'oid',
  'tfp',
  'acr',
  'amr',
]);

/**
 * CIAM / B2C often put the login id in extension_* or nested objects; walk claims after known fields.
 * @param {Record<string, unknown>} claims
 * @returns {string}
 */
function scanIdTokenClaimsForEmailLike(claims) {
  if (!claims || typeof claims !== 'object') return '';
  for (const [key, v] of Object.entries(claims)) {
    if (CLAIM_SCAN_SKIP_KEYS.has(key)) continue;
    if (typeof v === 'string') {
      const embedded = extractEmailFromCompoundLoginId(v);
      if (embedded) return embedded;
      if (isEmailLikeForAccountSwitcher(v)) return v.trim();
    } else if (Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === 'string') {
          const embedded = extractEmailFromCompoundLoginId(item);
          if (embedded) return embedded;
          if (isEmailLikeForAccountSwitcher(item)) return item.trim();
        } else if (item && typeof item === 'object') {
          for (const inner of Object.values(item)) {
            if (typeof inner === 'string') {
              const embedded = extractEmailFromCompoundLoginId(inner);
              if (embedded) return embedded;
              if (isEmailLikeForAccountSwitcher(inner)) return inner.trim();
            }
          }
        }
      }
    }
  }
  return '';
}

/**
 * @param {string} email
 * @returns {string}
 */
function displayNameFromEmailLocalPart(email) {
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/i;

/**
 * Stable per-account id when the token has no @ sign-in string (secondary line / disambiguation).
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string}
 */
export function getAccountStableIdLine(account) {
  if (!account) return '';
  const claims = account.idTokenClaims || {};
  const oid = typeof claims.oid === 'string' ? claims.oid.trim() : '';
  if (oid && UUID_RE.test(oid)) return oid;
  const lid = typeof account.localAccountId === 'string' ? account.localAccountId.trim() : '';
  if (lid && UUID_RE.test(lid)) return lid;
  return '';
}

/**
 * Lowercase Entra object id for POST /v1/users/display-by-oids.
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string}
 */
export function getAccountMongoLookupOid(account) {
  const s = getAccountStableIdLine(account);
  return s ? s.toLowerCase() : '';
}

/**
 * Best-effort **@** login / email for multi-account UI and Mongo lookup (never returns bare GUID without @).
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string}
 */
export function getSwitcherEmailDisplay(account) {
  if (!account) return '';
  const claims = account.idTokenClaims || {};
  const emailsArray = Array.isArray(claims.emails) ? claims.emails : [];
  const candidates = [
    ...emailsArray,
    claims.email,
    claims.preferred_username,
    claims.unique_name,
    claims.upn,
    account.username,
    account.email,
  ];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    const embedded = extractEmailFromCompoundLoginId(c);
    if (embedded) return embedded;
    if (isEmailLikeForAccountSwitcher(c)) return c.trim();
  }
  if (typeof claims.sub === 'string' && claims.sub.includes('@') && isEmailLikeForAccountSwitcher(claims.sub)) {
    return claims.sub.trim();
  }
  const scanned = scanIdTokenClaimsForEmailLike(claims);
  if (scanned) return scanned;
  return '';
}

/**
 * @param {string|null|undefined} name
 * @returns {boolean} true if name should not be shown as primary label
 */
export function isNonDisplayableAccountName(name) {
  if (!name || typeof name !== 'string') return true;
  const t = name.trim();
  if (!t) return true;
  const lower = t.toLowerCase();
  if (lower === 'unknown' || lower === 'anonymous' || lower === 'n/a' || lower === 'user') return true;
  return false;
}

/**
 * First human-readable email string from account (original casing).
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string}
 */
export function getPrimaryReadableEmail(account) {
  if (!account) return '';
  const claims = account.idTokenClaims || {};
  const emailsArray = Array.isArray(claims.emails) ? claims.emails : [];
  const candidates = [
    account.username,
    claims.preferred_username,
    claims.email,
    claims.upn,
    ...emailsArray,
    account.email,
  ];
  for (const c of candidates) {
    if (isLikelyUserEmail(c)) return String(c).trim();
  }
  return '';
}

/**
 * Normalized emails for matching profile / dedupe.
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string[]}
 */
export function collectNormalizedEmailsForAccount(account) {
  if (!account) return [];
  const set = new Set();
  const add = (c) => {
    if (!c || typeof c !== 'string') return;
    const x = normalizeEmailForMatch(c);
    if (x) set.add(x);
  };
  const switcher = getSwitcherEmailDisplay(account);
  if (switcher) add(switcher);
  const claims = account.idTokenClaims || {};
  const emailsArray = Array.isArray(claims.emails) ? claims.emails : [];
  for (const c of [account.username, claims.preferred_username, claims.email, claims.upn, ...emailsArray, account.email]) {
    if (isLikelyUserEmail(c)) add(String(c).trim());
  }
  const strict = getPrimaryReadableEmail(account);
  if (strict) add(strict);
  return [...set];
}

/**
 * @param {Record<string, unknown>|null|undefined} account
 * @param {string} normalizedProfileEmail from normalizeEmailForMatch
 * @returns {boolean}
 */
export function accountMatchesProfileEmail(account, normalizedProfileEmail) {
  if (!account || !normalizedProfileEmail) return false;
  return collectNormalizedEmailsForAccount(account).includes(normalizedProfileEmail);
}

/**
 * @param {Record<string, unknown>|null|undefined} a
 * @param {Record<string, unknown>|null|undefined} b
 * @returns {boolean}
 */
function accountsShareNormalizedEmail(a, b) {
  if (!a || !b) return false;
  const ea = collectNormalizedEmailsForAccount(a);
  const eb = collectNormalizedEmailsForAccount(b);
  if (!ea.length || !eb.length) return false;
  return ea.some((x) => eb.includes(x));
}

/**
 * Cached MSAL accounts to offer as "switch" targets: not the current session, not the same identity.
 * MSAL may keep two homeAccountIds for one Entra user; showing both produced a bogus "Switch" for the
 * account already signed in. Hide same oid / same email as active; use "Add account" for another Microsoft user.
 * @param {import('@azure/msal-browser').AccountInfo[]|null|undefined} allCached
 * @param {Record<string, unknown>|null|undefined} activeAccount
 * @param {Record<string, unknown>|null|undefined} excludedByResolver
 * @param {string} [profileNormalizedEmail]
 * @returns {import('@azure/msal-browser').AccountInfo[]}
 */
export function filterAccountsForSwitcherList(
  allCached,
  activeAccount,
  excludedByResolver,
  profileNormalizedEmail,
) {
  const list = Array.isArray(allCached) ? allCached : [];
  const activeHid =
    activeAccount && typeof activeAccount.homeAccountId === 'string' ? activeAccount.homeAccountId : '';
  const activeOid = getAccountMongoLookupOid(activeAccount);
  const excludedHid =
    excludedByResolver && typeof excludedByResolver.homeAccountId === 'string'
      ? excludedByResolver.homeAccountId
      : '';
  const profileNorm =
    typeof profileNormalizedEmail === 'string' && profileNormalizedEmail.trim()
      ? profileNormalizedEmail.trim().toLowerCase()
      : '';
  const seenOid = new Set();
  return list.filter((acc) => {
    const hid = typeof acc.homeAccountId === 'string' ? acc.homeAccountId : '';
    if (activeHid && hid === activeHid) return false;
    if (excludedHid && hid === excludedHid) return false;
    const oid = getAccountMongoLookupOid(acc);
    if (activeOid && oid && oid === activeOid) return false;
    if (profileNorm && accountMatchesProfileEmail(acc, profileNorm)) return false;
    if (activeAccount && accountsShareNormalizedEmail(activeAccount, acc)) return false;
    if (oid) {
      if (seenOid.has(oid)) return false;
      seenOid.add(oid);
    }
    return true;
  });
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} historical
 * @param {Record<string, unknown>|null|undefined} activeAccount
 * @param {string} [profileNormalizedEmail]
 * @returns {Array<Record<string, unknown>>}
 */
export function filterHistoricalAccountsForSwitcher(historical, activeAccount, profileNormalizedEmail) {
  const hist = Array.isArray(historical) ? historical : [];
  const activeHid =
    activeAccount && typeof activeAccount.homeAccountId === 'string' ? activeAccount.homeAccountId : '';
  const activeOid = getAccountMongoLookupOid(activeAccount);
  const profileNorm =
    typeof profileNormalizedEmail === 'string' && profileNormalizedEmail.trim()
      ? profileNormalizedEmail.trim().toLowerCase()
      : '';
  return hist.filter((acc) => {
    const hid = typeof acc.homeAccountId === 'string' ? acc.homeAccountId : '';
    if (activeHid && hid === activeHid) return false;
    const ho = getAccountMongoLookupOid(acc);
    if (activeOid && ho && ho === activeOid) return false;
    if (profileNorm && accountMatchesProfileEmail(acc, profileNorm)) return false;
    if (activeAccount && accountsShareNormalizedEmail(activeAccount, acc)) return false;
    return true;
  });
}

/**
 * Primary line for account rows (never prefers literal "unknown" over email).
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string}
 */
export function getAccountPrimaryLabel(account) {
  if (!account) return 'User';
  const claims = account.idTokenClaims || {};

  if (!isNonDisplayableAccountName(claims.name) && typeof claims.name === 'string') {
    return claims.name.trim();
  }
  if (!isNonDisplayableAccountName(account.name) && typeof account.name === 'string') {
    return account.name.trim();
  }
  if (!isNonDisplayableAccountName(claims.given_name) && typeof claims.given_name === 'string') {
    let out = claims.given_name.trim();
    if (!isNonDisplayableAccountName(claims.family_name) && typeof claims.family_name === 'string') {
      out += ` ${claims.family_name.trim()}`;
    }
    return out;
  }

  const email = getPrimaryReadableEmail(account);
  if (email) {
    return displayNameFromEmailLocalPart(email);
  }

  const switcherEmail = getSwitcherEmailDisplay(account);
  if (switcherEmail) {
    return displayNameFromEmailLocalPart(switcherEmail);
  }

  if (typeof account.username === 'string' && account.username.trim()) {
    return account.username.trim();
  }

  const stable = getAccountStableIdLine(account);
  if (stable) {
    return `Account ${stable.slice(0, 8)}…`;
  }
  return 'User';
}

/**
 * Secondary line (email / UPN).
 * @param {Record<string, unknown>|null|undefined} account
 * @returns {string}
 */
export function getAccountSecondaryLabel(account) {
  return getPrimaryReadableEmail(account);
}

/**
 * @param {string|null|undefined} label
 * @param {number} maxLen
 * @returns {string}
 */
export function getInitialsFromLabel(label, maxLen = 2) {
  if (!label || typeof label !== 'string') return 'U';
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().substring(0, maxLen);
  }
  const one = parts[0] || label;
  return one.substring(0, maxLen).toUpperCase();
}

/**
 * Account switcher row: prefer Mongo `users` name when API returned a row for this account's email.
 * @param {Record<string, unknown>|null|undefined} account
 * @param {Record<string, { name?: string, email?: string }>} mongoByNormEmail keys = normalizeEmailForMatch
 * @returns {{ primary: string, emailLine: string }}
 */
/**
 * @param {Record<string, unknown>|null|undefined} account
 * @param {Record<string, { name?: string, email?: string }>} mongoByNormEmail
 * @param {Record<string, string>|null|undefined} historyEmailByHomeId from getHistorySignInEmailByHomeAccountId()
 * @param {Record<string, { name?: string, email?: string }>|null|undefined} mongoByOid
 */
export function getAccountSwitcherRowLabels(account, mongoByNormEmail, historyEmailByHomeId, mongoByOid) {
  let emailFromToken = getSwitcherEmailDisplay(account);
  const hid = account && typeof account.homeAccountId === 'string' ? account.homeAccountId : '';
  if (
    !emailFromToken &&
    hid &&
    historyEmailByHomeId &&
    typeof historyEmailByHomeId === 'object' &&
    typeof historyEmailByHomeId[hid] === 'string'
  ) {
    const fromHist = historyEmailByHomeId[hid].trim();
    if (fromHist) emailFromToken = fromHist;
  }
  const norm = normalizeEmailForMatch(emailFromToken);
  const mongo =
    norm && mongoByNormEmail && typeof mongoByNormEmail === 'object' ? mongoByNormEmail[norm] : null;
  const oidKey = getAccountMongoLookupOid(account);
  const mongoOid =
    oidKey && mongoByOid && typeof mongoByOid === 'object' ? mongoByOid[oidKey] : null;
  const mongoName = mongo && typeof mongo.name === 'string' && mongo.name.trim() ? mongo.name.trim() : '';
  const mongoOidName =
    mongoOid && typeof mongoOid.name === 'string' && mongoOid.name.trim() ? mongoOid.name.trim() : '';
  const primary = mongoName || mongoOidName || getAccountPrimaryLabel(account);
  const emailLine =
    (emailFromToken && emailFromToken.trim()) ||
    (mongo && typeof mongo.email === 'string' && mongo.email.trim() ? mongo.email.trim() : '') ||
    (mongoOid && typeof mongoOid.email === 'string' && mongoOid.email.trim() ? mongoOid.email.trim() : '') ||
    getAccountStableIdLine(account);
  return { primary, emailLine };
}
